import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
    ArrowLeft, Buildings, Receipt, FileText, HandCoins, ArrowsClockwise,
    FileDoc, Clock, Scales, Hourglass, TrendUp, ShoppingCart, Lightning, Globe,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import OwnerCell from "../../components/OwnerCell";
import ReassignOwnerDialog from "../../components/ReassignOwnerDialog";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

import KpiTile from "./KpiTile";
import ActionableStrip from "./ActionableStrip";
import ClientHealthCard from "./ClientHealthCard";
import { PoLane, StandaloneLane } from "./PoLane";
import MilestoneQuickBillDialog from "./MilestoneQuickBillDialog";
import { STATUS_STYLE } from "./constants";

export default function ClientLedger() {
    const { id } = useParams();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const [ledger, setLedger] = useState(null);
    const [clientDoc, setClientDoc] = useState(null); // includes `owner` via /api/billing/client/{id}
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState("summary"); // "summary" | "detailed"
    const [quickBill, setQuickBill] = useState(null);
    const [reassignOpen, setReassignOpen] = useState(false);

    const load = async () => {
        try {
            const [l, h, c] = await Promise.all([
                api.get(`/billing/reports/client-ledger/${id}`),
                api.get(`/billing/reports/lifecycle-health`, { params: { client_id: id } }),
                api.get(`/billing/client/${id}`),
            ]);
            setLedger(l.data);
            setHealth(h.data);
            setClientDoc(c.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load ledger"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    // Group lifecycle events by PO; events without po_id go to "unassigned"
    const grouped = useMemo(() => {
        if (!ledger) return { byPo: new Map(), orphan: [] };
        const byPo = new Map();
        const orphan = [];
        for (const ev of ledger.lifecycle_events || []) {
            if (ev.po_id) {
                if (!byPo.has(ev.po_id)) byPo.set(ev.po_id, []);
                byPo.get(ev.po_id).push(ev);
            } else {
                orphan.push(ev);
            }
        }
        return { byPo, orphan };
    }, [ledger]);

    // Running-balance ledger for Detailed mode
    const runningLedger = useMemo(() => {
        if (!ledger) return [];
        const events = [];
        for (const inv of ledger.invoices) {
            events.push({ kind: "invoice", date: inv.invoice_date, label: inv.invoice_number, amount: inv.total_amount, sign: +1 });
        }
        for (const p of ledger.payments) {
            events.push({ kind: "payment", date: p.payment_date, label: `Payment · ${p.payment_mode || "—"}${p.reference_number ? ` · ${p.reference_number}` : ""}`, amount: p.amount, sign: -1 });
        }
        for (const cn of ledger.credit_notes) {
            events.push({ kind: "credit_note", date: cn.cn_date, label: `${cn.cn_number} · ${(cn.reason_code || "").replace(/_/g, " ")}`, amount: cn.total_amount, sign: -1 });
        }
        events.sort((a, b) => new Date(a.date) - new Date(b.date));
        let bal = 0;
        return events.map((e) => {
            bal = Math.round((bal + e.sign * e.amount) * 100) / 100;
            return { ...e, balance: bal };
        }).reverse();
    }, [ledger]);

    const openQuickBill = (ev) => {
        if (!ledger) return;
        const po = ledger.pos.find((p) => p.id === ev.po_id);
        if (!po) { toast.error("PO not found in this ledger"); return; }
        const milestone = (po.payment_schedule || []).find((m) => m.id === ev.milestone_id);
        if (!milestone) { toast.error("Milestone not found on this PO"); return; }
        setQuickBill({ po, milestone });
    };

    if (loading) return <div data-testid="client-ledger-page"><PageHeader eyebrow="Client ledger" title="Loading…" /></div>;
    if (!ledger) return <div className="p-8 text-sm text-slate-500">Client not found.</div>;

    const { client, totals, pos } = ledger;

    return (
        <div data-testid="client-ledger-page">
            <PageHeader
                eyebrow={
                    <Link to="/clients" className="inline-flex items-center gap-1 hover:text-slate-900">
                        <ArrowLeft size={12} /> Clients
                    </Link>
                }
                title={
                    <span className="flex items-center gap-3 flex-wrap">
                        <Buildings size={22} className="text-slate-400" />
                        <span>{client.name}</span>
                        {client.gstin && <span className="font-mono text-sm text-slate-400">{client.gstin}</span>}
                        {client.is_international && (
                            <span
                                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold border bg-amber-50 text-amber-700 border-amber-200"
                                title={client.country ? `${client.country} · GST not applicable (export billing)` : "GST not applicable / export billing"}
                                data-testid="ledger-client-international-chip"
                            >
                                <Globe size={11} weight="fill" /> International
                            </span>
                        )}
                    </span>
                }
                description={
                    <span className="inline-flex items-center gap-2 text-xs flex-wrap">
                        {client.state_code && <span><span className="reedos-eyebrow">State</span> <span className="ml-1 font-mono">{client.state_code}</span></span>}
                        {client.contact_name && <><span className="text-slate-300">·</span><span>{client.contact_name}</span></>}
                        {client.contact_email && <><span className="text-slate-300">·</span><a href={`mailto:${client.contact_email}`} className="hover:text-slate-900 underline">{client.contact_email}</a></>}
                        {clientDoc?.owner && (
                            <>
                                <span className="text-slate-300">·</span>
                                <span className="reedos-eyebrow">Owner</span>
                                <OwnerCell
                                    owner={clientDoc.owner}
                                    testId="ledger-client-owner"
                                    onReassignClick={isSuperAdmin ? () => setReassignOpen(true) : undefined}
                                />
                            </>
                        )}
                    </span>
                }
                actions={
                    <div className="inline-flex items-center rounded-md border border-slate-200 bg-white p-0.5" data-testid="ledger-mode-toggle">
                        <ModeBtn active={mode === "summary"} onClick={() => setMode("summary")} testid="ledger-mode-summary">Summary (CEO)</ModeBtn>
                        <ModeBtn active={mode === "detailed"} onClick={() => setMode("detailed")} testid="ledger-mode-detailed">Detailed (Ops/Finance)</ModeBtn>
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="ledger-kpis">
                    <KpiTile label="PO Value" value={formatINR(totals.total_po_value)} icon={<ShoppingCart size={12} />} tone="indigo" testid="kpi-po-value" sub={`${totals.pos_finalized} finalized`} />
                    <KpiTile label="Invoiced" value={formatINR(totals.invoiced)} icon={<Receipt size={12} />} testid="kpi-invoiced" sub={`${totals.invoices_count} invoice${totals.invoices_count !== 1 ? "s" : ""}`} />
                    <KpiTile label="Received" value={formatINR(totals.paid)} tone="emerald" icon={<HandCoins size={12} />} testid="kpi-paid" />
                    <KpiTile label="TDS Receivable" value={formatINR(totals.tds_receivable || 0)} tone={totals.tds_receivable > 0 ? "amber" : "slate"} icon={<HandCoins size={12} />} testid="kpi-tds-receivable" sub="Tax credit, not outstanding" />
                    <KpiTile label="Outstanding" value={formatINR(totals.outstanding)} tone={totals.outstanding > 0 ? "rose" : "emerald"} icon={<Scales size={12} />} testid="kpi-outstanding" sub="Excludes TDS" />
                    <KpiTile label="Unbilled" value={formatINR(totals.unbilled)} tone={totals.unbilled > 0 ? "amber" : "emerald"} icon={<Hourglass size={12} />} testid="kpi-unbilled" sub="PO total minus invoiced" />
                    <KpiTile label="Settle amount" value={formatINR(totals.settle_amount)} tone="rose" icon={<TrendUp size={12} />} testid="kpi-settle" sub={`Outstanding + ${formatINR(totals.interest_accrued)} interest`} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <ActionableStrip items={ledger.actionable} summary={ledger.actionable_summary} />
                    </div>
                    <ClientHealthCard health={health} />
                </div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="reedos-eyebrow flex items-center gap-1.5"><Lightning size={11} weight="fill" /> Commercial Lifecycle Timeline</div>
                            <h3 className="font-display text-lg font-semibold text-slate-900 mt-0.5">
                                {pos.length} PO{pos.length !== 1 ? "s" : ""} · {ledger.lifecycle_events?.length || 0} events
                            </h3>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                                Each PO is a lane: Proposal → Created → Finalized → Milestone Invoices → Payments. Click any node to drill in.
                            </p>
                        </div>
                    </div>

                    {pos.length === 0 && grouped.orphan.length === 0 ? (
                        <EmptyState icon={<FileText size={36} weight="thin" />} title="No commercial activity yet" />
                    ) : (
                        <div className="space-y-4" data-testid="lifecycle-timeline">
                            {pos.map((po) => (
                                <PoLane key={po.id} po={po} events={grouped.byPo.get(po.id) || []} onQuickBill={openQuickBill} />
                            ))}
                            <StandaloneLane events={grouped.orphan} label="Other events (proposals & ad-hoc)" />
                        </div>
                    )}
                </div>

                {mode === "detailed" && (
                    <>
                        <InvoicesTable totals={totals} invoices={ledger.invoices} />
                        <RunningBalance events={runningLedger} />
                    </>
                )}
            </div>

            <MilestoneQuickBillDialog
                open={!!quickBill}
                onOpenChange={(v) => { if (!v) setQuickBill(null); }}
                po={quickBill?.po}
                milestone={quickBill?.milestone}
                onCreated={() => { setQuickBill(null); load(); }}
            />
            <ReassignOwnerDialog
                open={reassignOpen}
                onOpenChange={setReassignOpen}
                entityLabel="Client"
                entityName={client.name}
                currentOwner={clientDoc?.owner}
                reassignUrl={`/billing/client/${id}/owner`}
                onSuccess={load}
                testIdPrefix="ledger-client-reassign"
            />
        </div>
    );
}

function ModeBtn({ active, onClick, testid, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={classJoin("px-3 py-1.5 text-xs font-semibold rounded transition-colors",
                active ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900")}
            data-testid={testid}
        >
            {children}
        </button>
    );
}

function InvoicesTable({ totals, invoices }) {
    return (
        <div className="reedos-card" data-testid="ledger-invoices">
            <div className="px-5 py-4 border-b border-slate-200">
                <div className="reedos-eyebrow">Invoices · finance detail</div>
                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">{totals.invoices_count} raised</h3>
            </div>
            {invoices.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-slate-500">No invoices.</div>
            ) : (
                <table className="w-full text-sm" data-testid="ledger-invoices-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left">
                            <th className="px-5 py-3 reedos-eyebrow">Invoice</th>
                            <th className="px-5 py-3 reedos-eyebrow">Date</th>
                            <th className="px-5 py-3 reedos-eyebrow">Due</th>
                            <th className="px-5 py-3 reedos-eyebrow text-right">Total</th>
                            <th className="px-5 py-3 reedos-eyebrow text-right">Paid</th>
                            <th className="px-5 py-3 reedos-eyebrow text-right">TDS</th>
                            <th className="px-5 py-3 reedos-eyebrow text-right">Credits</th>
                            <th className="px-5 py-3 reedos-eyebrow text-right">Outstanding</th>
                            <th className="px-5 py-3 reedos-eyebrow">Status</th>
                            <th className="px-5 py-3 reedos-eyebrow text-right">Interest</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoices.map((inv) => (
                            <tr key={inv.id} className="border-t border-slate-100 reedos-row-hover">
                                <td className="px-5 py-3">
                                    <Link to={`/invoices/${inv.id}`} className="font-mono text-xs font-bold text-slate-900 hover:underline">{inv.invoice_number}</Link>
                                </td>
                                <td className="px-5 py-3 text-xs text-slate-600">{formatDate(inv.invoice_date)}</td>
                                <td className="px-5 py-3 text-xs text-slate-600">{formatDate(inv.due_date)}</td>
                                <td className="px-5 py-3 text-right reedos-data">{formatINR(inv.total_amount)}</td>
                                <td className="px-5 py-3 text-right reedos-data text-emerald-700">{formatINR(inv.paid_amount)}</td>
                                <td className="px-5 py-3 text-right reedos-data text-amber-700">{(inv.tds_amount || 0) > 0 ? formatINR(inv.tds_amount) : "—"}</td>
                                <td className="px-5 py-3 text-right reedos-data text-rose-700">{inv.credit_note_total > 0 ? `-${formatINR(inv.credit_note_total)}` : "—"}</td>
                                <td className="px-5 py-3 text-right reedos-data font-semibold">{formatINR(inv.outstanding)}</td>
                                <td className="px-5 py-3">
                                    <span className={classJoin("inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold border", STATUS_STYLE[inv.status])}>
                                        {inv.status}
                                    </span>
                                    {inv.is_overdue && (
                                        <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-rose-700">
                                            <Clock size={10} /> {inv.days_overdue}d
                                        </span>
                                    )}
                                </td>
                                <td className="px-5 py-3 text-right reedos-data text-xs text-amber-700">{inv.interest_accrued > 0 ? formatINR(inv.interest_accrued) : "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function RunningBalance({ events }) {
    return (
        <div className="reedos-card" data-testid="ledger-running-balance">
            <div className="px-5 py-4 border-b border-slate-200">
                <div className="reedos-eyebrow">Chronological ledger · running balance</div>
                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">{events.length} events</h3>
            </div>
            {events.length === 0 ? (
                <EmptyState icon={<FileText size={36} weight="thin" />} title="No activity yet" />
            ) : (
                <ul className="divide-y divide-slate-100">
                    {events.map((e, idx) => {
                        const tone =
                            e.kind === "invoice" ? "text-slate-900" :
                                e.kind === "payment" ? "text-emerald-700" :
                                    "text-rose-700";
                        const Icon = e.kind === "invoice" ? Receipt : e.kind === "payment" ? HandCoins : ArrowsClockwise;
                        const ringCls = tone === "text-emerald-700" ? "border-emerald-200 bg-emerald-50" :
                            tone === "text-rose-700" ? "border-rose-200 bg-rose-50" :
                                "border-slate-200 bg-slate-50";
                        return (
                            <li key={`${e.kind}-${idx}`} className="px-5 py-3 grid grid-cols-[auto,1fr,auto,auto] gap-4 items-center" data-testid={`ledger-event-${idx}`}>
                                <div className={classJoin("h-8 w-8 rounded-full border flex items-center justify-center", ringCls)}>
                                    <Icon size={14} className={tone} weight="fill" />
                                </div>
                                <div className="min-w-0">
                                    <div className={classJoin("text-sm font-medium truncate", tone)}>{e.label}</div>
                                    <div className="text-[11px] text-slate-500 mt-0.5">{formatDate(e.date)}</div>
                                </div>
                                <div className={classJoin("reedos-data text-sm font-semibold whitespace-nowrap", tone)}>
                                    {e.sign > 0 ? "" : "- "}{formatINR(e.amount)}
                                </div>
                                <div className="text-right whitespace-nowrap">
                                    <div className="reedos-eyebrow">Balance</div>
                                    <div className={classJoin("reedos-data font-bold", e.balance > 0 ? "text-rose-700" : "text-slate-900")}>{formatINR(e.balance)}</div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
